import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FIELD_QUANTITY } from '@components/dynamic-input-fields/src/utils';
import { IconOfflineSale, IconPerson, IconUsers } from '@components/icons';
import { InputCounter } from '@components/input-counter';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import { MultiselectOption } from '@components/multiselect/multiselect.type';
import {
  MULTIPLE_OFFLINE_SELL,
  OfflineSalesForm,
  SaleType,
  SINGLE_OFFLINE_SELL,
} from '@components/process-offline-sales/src/process-offline-sales.type';
import { checkBookingFormFieldsAllowed } from '@components/process-offline-sales/utils';
import { SORT_ASC } from '@constants/global';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { Admission } from '@domain/admission';
import { ActiveChannel } from '@domain/channel';
import { EventListItem } from '@domain/event';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useGetAdmissions } from '@hooks/admissions/use-get-admissions';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetEventList } from '@hooks/events/use-get-event-list';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { EventView } from '@hooks/use-event-navigation/use-event-navigation';
import { Divider } from '@tremor/react';
import { calculateChannelMaxQuantityPerTransaction } from '@utils/calculate-max-items-per-transation/calculate-max-items-per-transation';
import moment from 'moment';

import { ControlledMultiOfferSelector } from '../multi-offer/controlled-multi-offer-selector';
import { createMultiSelectorOption } from '../utils/create-multi-selector-option';

import { SelectTypeForm } from './select-type-form';
import { SelectTypeSkeleton } from './select-type-skeleton';

export const SelectType = ({
  onContinue,
  offer,
  parentRef,
}: {
  onContinue: () => void;
  offer?: Admission;
  parentRef?: React.RefObject<HTMLDivElement | HTMLFormElement>;
}) => {
  const { t } = useTranslation();
  const {
    companyClubState: { isSiaeImportLimited, isImportLimited },
    companyState: { id: companyId },
    clubState: {
      id: clubId,
      city: { timezone },
    },
  } = useContext(CompanyClubContext);
  const { role } = useContext(CompanyAuthContext);
  const {
    formState: { isValid },
    watch,
    setValue,
  } = useFormContext<OfflineSalesForm>();

  const [channelList, setChannelList] = useState<ActiveChannel[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<MultiselectOption[]>([]);

  const eventId = watch('eventId');
  const offerId = watch('offerId');
  const channelId = watch('channelId');
  const saleType = watch('saleType');

  const todayNow = useMemo(() => moment().tz(timezone).unix(), [timezone]);
  const {
    data: eventList,
    isLoading: isEventListLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetEventList({
    companyId,
    clubId,
    view: 'all' as EventView,
    params: {
      startTime: todayNow,
      sort: SORT_ASC,
      orderBy: 'date',
      limit: 50,
    },
  });
  const { data: admissionList } = useGetAdmissions({ eventId, options: { enabled: !!eventId } });
  const { data: addOnsList } = useGetOfferAddon({ offerId, enabled: !!offerId });
  const availableAddons = addOnsList?.filter((addon) => {
    if (!addon.quantity) return true;

    return addon.soldItems < addon.quantity;
  });
  const { data: activeChannels } = useGetActiveChannels({
    offerId,
    enabled: !!offerId,
  });
  const { data: eventChannels } = useGetChannels({ eventId, enabled: !!eventId && !!offerId });

  const toggleSaleType = (type: SaleType) => {
    if (saleType === type) return;
    setValue('saleType', type, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    // we will calculate quantity on multi offline text field
    if (type === MULTIPLE_OFFLINE_SELL) setValue('quantity', 0);
  };

  const showAddons = !!offer && offer.addOns.length > 0;
  const isPermittedMultiOfflineSale =
    offer && !offer.ticketHolderFormFields.length && checkBookingFormFieldsAllowed(offer.bookingFormFields);

  const eventListFiltered = (eventList?.pages as EventListItem[][] | undefined)?.map((page) =>
    page.filter((event) => !event.settings?.isHidden)
  );
  const typeOptions = useMemo(() => {
    return [
      createMultiSelectorOption({
        id: SINGLE_OFFLINE_SELL,
        name: t('offers:singleOfferTitle'),
        subtitle: t('offers:singleOffersSubtitle'),
        icon: IconPerson,
        value: saleType,
        toggler: toggleSaleType,
      }),
      createMultiSelectorOption({
        id: MULTIPLE_OFFLINE_SELL,
        name: t('offers:multiOfferTitle'),
        subtitle: t('offers:multiOfferTitleSubtitle'),
        icon: IconUsers,
        value: saleType,
        toggler: toggleSaleType,
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, saleType]);

  if (hasNextPage) fetchNextPage();

  useEffect(() => {
    if (eventChannels && activeChannels && offer) {
      const channelsFormatted = eventChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          maxQuantity: null,
        }));

      if (activeChannels.isForAllChannels) {
        setChannelList(channelsFormatted);
        return;
      }

      setChannelList(activeChannels.channels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannels, eventChannels, offer]);

  return (
    <>
      <ModalHeader title={t('offers:offlineSalesTitle')} primaryIcon={IconOfflineSale} />
      <div className='flex flex-col gap-4 px-4 py-6 sm:px-20 sm:pb-12'>
        {isEventListLoading ? (
          <SelectTypeSkeleton rows={eventId ? 2 : 1} />
        ) : (
          <>
            <SelectTypeForm
              eventListFiltered={eventListFiltered}
              admissionList={admissionList}
              channelList={channelList}
              addOnsList={availableAddons}
              isImportLimited={isImportLimited}
              isSiaeImportLimited={isSiaeImportLimited}
              showAddons={showAddons}
              timezone={timezone}
              parentRef={parentRef}
              role={role}
              offer={offer}
              selectedAddons={selectedAddons}
              setSelectedAddons={setSelectedAddons}
            />
            {eventId && offerId && channelId && (
              <>
                <Divider className='my-0' />
                {isPermittedMultiOfflineSale && (
                  <ControlledMultiOfferSelector formName='saleType' typeOptions={typeOptions} type={saleType} />
                )}
                {saleType === SINGLE_OFFLINE_SELL && offer && (
                  <InputCounter
                    formCounterName={FIELD_QUANTITY}
                    minQuantity={offer.itemsPerTransaction.min ?? 0}
                    maxQuantity={calculateChannelMaxQuantityPerTransaction(offer)}
                    title={t('invitations:totalGuestCounterText')}
                    error={t('errors:minQuantityFieldError', { number: offer.itemsPerTransaction?.min })}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      <ModalFooter
        primaryButtonText={t('offers:continueButton')}
        onPrimaryClick={onContinue}
        isPrimaryDisabled={isEventListLoading || !isValid}
      />
    </>
  );
};
